import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const payrollPeriodsData = [
  { id: "1", period: "July 2025", status: "Pending", totalAmount: "1,25,00,000" },
  { id: "2", period: "June 2025", status: "Approved", totalAmount: "1,24,50,000" },
  { id: "3", period: "May 2025", status: "Approved", totalAmount: "1,23,80,000" },
  { id: "4", period: "April 2025", status: "Approved", totalAmount: "1,23,50,000" },
];

const PayrollPage = ({ onBack }) => {
  const [payrollPeriods, setPayrollPeriods] = useState(payrollPeriodsData);
  const [processModalOpen, setProcessModalOpen] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  const handleRunPayroll = (period) => {
    setCurrentPeriod(period);
    setProcessModalOpen(true);
  };

  const confirmRunPayroll = () => {
    if (currentPeriod) {
      setPayrollPeriods(
        payrollPeriods.map((p) =>
          p.id === currentPeriod.id ? { ...p, status: "Processing" } : p
        )
      );
      setTimeout(() => {
        setPayrollPeriods(
          payrollPeriods.map((p) =>
            p.id === currentPeriod.id ? { ...p, status: "Approved" } : p
          )
        );
      }, 3000);
    }
    setProcessModalOpen(false);
    setCurrentPeriod(null);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Approved":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "Processing":
        return "bg-sky-50 text-sky-700 ring-sky-600/20";
      default:
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 bg-gradient-to-br from-sky-100 via-indigo-50 to-cyan-100 font-sans text-gray-800">
      <motion.div
        className="max-w-6xl mx-auto bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-5 sm:p-8 border border-gray-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-5 flex items-center text-indigo-600 hover:text-indigo-800 transition-colors text-sm font-medium"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Payroll Management
            </h1>
            <p className="text-gray-500 mt-1 text-xs sm:text-sm">
              Process monthly salaries, bonuses, and tax deductions seamlessly.
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center">
            <div className="p-2 bg-emerald-100 rounded-lg mr-3">
              <CurrencyRupeeIcon className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs">Current Payroll</p>
              <h3 className="text-lg font-semibold text-gray-800">₹1.25 Cr</h3>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg mr-3">
              <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs">Pending Periods</p>
              <h3 className="text-lg font-semibold text-gray-800">1</h3>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center">
            <div className="p-2 bg-rose-100 rounded-lg mr-3">
              <XCircleIcon className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <p className="text-gray-500 text-xs">Errors</p>
              <h3 className="text-lg font-semibold text-gray-800">0</h3>
            </div>
          </div>
        </div>

        {/* Payroll Periods */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">
            Recent Periods
          </h2>
          {payrollPeriods.map((period) => (
            <motion.div
              key={period.id}
              className="bg-white p-3 rounded-lg shadow-sm border flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.3,
                delay: 0.05 * parseInt(period.id),
              }}
            >
              <div className="flex-1 sm:flex sm:items-center sm:space-x-4 mb-2 sm:mb-0">
                <span className="font-medium text-sm sm:text-base text-gray-800 w-28">
                  {period.period}
                </span>
                <span className="text-gray-600 text-xs sm:text-sm flex items-center">
                  <CurrencyRupeeIcon className="h-4 w-4 mr-1 text-gray-500" />
                  {period.totalAmount}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {/* Status Badge */}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusStyles(
                    period.status
                  )}`}
                >
                  {period.status === "Approved" && (
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                  )}
                  {period.status === "Processing" && (
                    <span className="animate-pulse mr-1">...</span>
                  )}
                  {period.status}
                </span>

                {/* Run Button */}
                {period.status === "Pending" && (
                  <motion.button
                    onClick={() => handleRunPayroll(period)}
                    className="bg-emerald-500 text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-emerald-600 shadow-sm flex items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <PlayIcon className="h-4 w-4 mr-1" />
                    Run
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {processModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/30 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-5 rounded-xl shadow-lg w-full max-w-xs mx-4 border border-gray-100"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
            >
              <div className="text-center">
                <div className="p-2 bg-emerald-100 rounded-full mx-auto w-10 h-10 flex items-center justify-center mb-2">
                  <CurrencyRupeeIcon className="h-5 w-5 text-emerald-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  Confirm Payroll
                </h2>
                <p className="text-gray-500 text-xs mb-3">
                  Process payroll for{" "}
                  <span className="font-semibold">
                    {currentPeriod?.period}
                  </span>
                  ?
                </p>
              </div>

              <div className="flex justify-between space-x-2">
                <button
                  onClick={() => setProcessModalOpen(false)}
                  className="flex-1 px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:text-gray-800 transition bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRunPayroll}
                  className="flex-1 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white transition"
                >
                  Run
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PayrollPage;
